// Packages
import React, { useState, useEffect, useMemo } from "react";
import { useIntl, FormattedMessage } from 'react-intl';
// Icons, Images etc.
import NoImageAvailableIcon from "../../images/NoImageAvailableIcon.png";
// Helpers, Utils etc.
import { formatDecimal } from "../../helpers/itemCalculations";
import { orderItems } from "../../helpers/orderPayloadBuilder";
import {
  translatedName,
} from "../../helpers/translations";
import OutOfStockWarning from "../outOfStockWarning";
import { outOfStockItemsList } from "../../helpers/cartFunctions";
import { isItemOutOfStock } from "../../helpers/utils";
import { QUALITY_20, transformImageQuality } from "../../helpers/imageTransformation";
import { usePromotionsContext } from "../../hooks/usePromotionsContext";
import { combineCarts } from "../../features/promotions/helpers/utils";

interface ICartItemsWidgetProps {
  cart: any;
  currency: any;
  outOfStockItems: any;
}

function CartItemsWidget(props: ICartItemsWidgetProps) {

  const { freeItemCart, appliedPromotionsData } = usePromotionsContext();
  const promotionAppliedCart = appliedPromotionsData.promotionAppliedCart;

  const userCartInfo = useMemo(() => {
    return {
      ...props.cart,
      items: combineCarts(promotionAppliedCart, freeItemCart)
    }
  },[JSON.stringify(promotionAppliedCart), JSON.stringify(freeItemCart)]);

  const cart = useMemo(() => {
    if (userCartInfo) {
      let updatedCart = Object.assign({}, userCartInfo);
      updatedCart.items = orderItems(updatedCart.items);
      return updatedCart;
    }
    return null;
  }, [userCartInfo]);

  const intl = useIntl();

  const outOfStockCartItems = useMemo(()=>{
    return outOfStockItemsList(props.cart.items, props.outOfStockItems)
  }, [props.cart.items, props.outOfStockItems])

  return (
    cart && cart.items.length ? (
      <div className="past-order-details no-padding">
        <h1 className="section-heading">
          <FormattedMessage
            id="order.summary"
            defaultMessage="Order Summary"
          />
        </h1>
        {outOfStockCartItems.length ? (
          <div className="out-of-stock-message">
            <OutOfStockWarning outOfStockItems={outOfStockCartItems} />
          </div>
        ) : null}
        <table className="order-details-table">
          <thead>
            <tr>
              <th className="name">
                <FormattedMessage
                  id="global.item"
                  defaultMessage="Item"
                />
              </th>
              <th>
                <FormattedMessage
                  id="global.quantity"
                  defaultMessage="Quantity"
                />
              </th>
              <th>
                <FormattedMessage
                  id="global.price"
                  defaultMessage="Price"
                />
              </th>
              <th>
                <FormattedMessage
                  id="global.total"
                  defaultMessage="Total"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {cart.items.map((item: any) => {
              const itemOutOfStock = isItemOutOfStock(item, props.outOfStockItems)
              return (
                <tr key={item.cart_id} className={itemOutOfStock ? "item-disabled" : ""}>
                  <td
                    className="name"
                    style={
                      item.modifiers.length
                        ? { verticalAlign: "top" }
                        : { verticalAlign: "center" }
                    }
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className={
                          item.image_url ? "item-image" : "item-image hidden"
                        }
                        style={{
                          backgroundImage: `url(${
                            item.image_url
                              ? transformImageQuality(item.image_url, QUALITY_20)
                              : NoImageAvailableIcon
                          })`,
                        }}
                      />
                      <div>
                        <p>{translatedName(item, intl.locale)}</p>
                        {item.modifiers.length ? (
                          <div className="modifiers">
                            {item.modifiers.map((modifier: any, index: number) => {
                              return (
                                <p className="modifier-item" key={index}>
                                  {translatedName(modifier, intl.locale)}
                                  {modifier.unit_price > 0 ? (
                                    <span className="price">
                                      +{formatDecimal(modifier.unit_price)}{" "}
                                      {props.currency}
                                    </span>
                                  ) : " "}
                                  {"x " + modifier.quantity}
                                </p>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td
                    style={
                      item.modifiers.length
                        ? { verticalAlign: "top" }
                        : { verticalAlign: "center" }
                    }
                  >
                    {item.quantity}
                  </td>
                  <td
                    style={
                      item.modifiers.length
                        ? { verticalAlign: "top" }
                        : { verticalAlign: "center" }
                    }
                  >
                    {formatDecimal(item.sub_total / item.quantity)}
                  </td>
                  <td
                    style={
                      item.modifiers.length
                        ? { verticalAlign: "top" }
                        : { verticalAlign: "center" }
                    }
                  >
                    {formatDecimal(item.sub_total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      null
    )
  );
}

export default CartItemsWidget;
